/// <reference types="google.maps" />
import { loadGoogleMapsScript, waitForGoogleMaps } from './googleMaps'

export interface GeocodeResult {
  lat: number
  lng: number
  formatted_address: string
  address_components: {
    street_number?: string
    route?: string
    locality?: string
    administrative_area_level_1?: string
    postal_code?: string
    country?: string
  }
  partial_match?: boolean
}

/**
 * Geocode an address to get coordinates
 * @param address - Address string to geocode
 * @returns Promise with geocoding result
 */
export async function geocodeAddress(
  address: string
): Promise<GeocodeResult | null> {
  await waitForGoogleMaps()

  return new Promise((resolve, reject) => {
    if (!window.google?.maps) {
      reject(new Error('Google Maps API not loaded'))
      return
    }

    const geocoder = new google.maps.Geocoder()

    geocoder.geocode({ address }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
      if (status === 'OK' && results && results.length > 0) {
        const result = results[0]

        // Check for partial match
        if (result.partial_match) {
          resolve({
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng(),
            formatted_address: result.formatted_address,
            address_components: extractAddressComponents(result),
            partial_match: true,
          })
          return
        }

        // Valid result
        resolve({
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
          formatted_address: result.formatted_address,
          address_components: extractAddressComponents(result),
          partial_match: false,
        })
      } else {
        reject(
          new Error(
            `Geocoding failed: ${status === 'ZERO_RESULTS' ? 'Address not found' : status}`
          )
        )
      }
    })
  })
}

/**
 * Validate address with ZIP code and state
 * @param address - Address string
 * @param zipCode - Expected ZIP code
 * @param state - Expected state code
 * @returns Promise with validation result
 */
export async function validateAddress(
  address: string,
  zipCode?: string,
  state?: string
): Promise<{
  valid: boolean
  lat?: number
  lng?: number
  message?: string
  partial_match?: boolean
}> {
  try {
    const result = await geocodeAddress(address)

    if (!result) {
      return {
        valid: false,
        message: 'Unable to verify address',
      }
    }

    // Check for partial match
    if (result.partial_match) {
      return {
        valid: false,
        lat: result.lat,
        lng: result.lng,
        message: 'Warning: Address may be incomplete or ambiguous',
        partial_match: true,
      }
    }

    // Validate ZIP code if provided
    if (zipCode && result.address_components.postal_code !== zipCode) {
      return {
        valid: false,
        lat: result.lat,
        lng: result.lng,
        message: 'ZIP code does not match address',
      }
    }

    // Validate state if provided
    if (
      state &&
      result.address_components.administrative_area_level_1 !== state
    ) {
      return {
        valid: false,
        lat: result.lat,
        lng: result.lng,
        message: 'State does not match address',
      }
    }

    return {
      valid: true,
      lat: result.lat,
      lng: result.lng,
    }
  } catch (error) {
    return {
      valid: false,
      message:
        error instanceof Error ? error.message : 'Address validation failed',
    }
  }
}

/**
 * Extract address components from geocoding result
 */
/**
 * Raw Geocoder call (same as Angular company-edit geocodeAddress: google.maps.Geocoder).
 */
export async function geocodeAddressRaw(address: string): Promise<{
  result: google.maps.GeocoderResult | null
  status: google.maps.GeocoderStatus
}> {
  await loadGoogleMapsScript()
  await waitForGoogleMaps()

  return new Promise((resolve) => {
    if (!window.google?.maps) {
      resolve({ result: null, status: google.maps.GeocoderStatus.REQUEST_DENIED })
      return
    }
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ address }, (results, status) => {
      resolve({
        result: results && results.length > 0 ? results[0] : null,
        status,
      })
    })
  })
}

/**
 * Zip + state checks matching Angular company-edit geocodeAddress (Object.keys over address_components).
 */
/** Angular `resident-application-add` `geocodeAddress()` city/state/zip checks. */
export function matchCityStateZipResidentGeocodeStyle(
  place: google.maps.GeocoderResult,
  city: string,
  state: string,
  zipCode: string,
): boolean {
  if (!place.formatted_address) return false;

  const formattedLower = place.formatted_address.toLowerCase();
  const cityTrimmed = String(city ?? '').trim();
  const stateTrimmed = String(state ?? '').trim();
  const zipTrimmed = String(zipCode ?? '').trim();

  const cityInFormatted = formattedLower.includes(cityTrimmed.toLowerCase());
  const cityInPostcodeLocalities = Boolean(
    place.postcode_localities?.length &&
      place.postcode_localities.includes(cityTrimmed),
  );
  const cityValid = cityInFormatted || cityInPostcodeLocalities;
  const stateValid = formattedLower.includes(stateTrimmed.toLowerCase());
  const zipValid = place.formatted_address.includes(zipTrimmed);

  return cityValid && stateValid && zipValid;
}

export function matchZipAndStateAngularStyle(
  place: google.maps.GeocoderResult,
  companyZipcode: string,
  companyState: string
): { zipValid: boolean; stateValid: boolean } {
  let zipValid = false
  let stateValid = false
  const formZip = String(companyZipcode ?? '').trim()
  const formState = String(companyState ?? '').trim()
  if (place.address_components) {
    Object.keys(place.address_components).forEach((key) => {
      const comp = place.address_components[Number(key)]
      if (!comp?.types) return
      if (
        comp.types.includes('postal_code') &&
        comp.long_name === formZip
      ) {
        zipValid = true
      }
      if (
        comp.types.includes('administrative_area_level_1') &&
        comp.short_name === formState
      ) {
        stateValid = true
      }
    })
  }
  return { zipValid, stateValid }
}

function extractAddressComponents(
  result: google.maps.GeocoderResult
): GeocodeResult['address_components'] {
  const components: GeocodeResult['address_components'] = {}

  if (result.address_components) {
    result.address_components.forEach((component: google.maps.GeocoderAddressComponent) => {
      const types = component.types

      if (types.includes('street_number')) {
        components.street_number = component.long_name
      }
      if (types.includes('route')) {
        components.route = component.long_name
      }
      if (types.includes('locality')) {
        components.locality = component.long_name
      }
      if (types.includes('administrative_area_level_1')) {
        components.administrative_area_level_1 = component.short_name
      }
      if (types.includes('postal_code')) {
        components.postal_code = component.long_name
      }
      if (types.includes('country')) {
        components.country = component.short_name
      }
    })
  }

  return components
}

